import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import client from '../../lib/apolloClient';
import { TextField, Card, CardContent, Typography, Grid, Button, Select, MenuItem, LinearProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ListCard from './ListCard';
import SearchSidebar from './SearchSidebar';
import { 
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Slider,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  IconButton,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import SearchIcon from '@material-ui/icons/Search';
import { regionForCountry } from '../../lib/findBobUtils';

export const USER_SEARCH_QUERY = gql`
  query UserSearch(
    $prompt: String, 
    $page: Int, 
    $perPage: Int, 
    $locationWithin: String,
    $transitionGoalsName: [String!],
    $planningPreference: String,
    $province: String,
    $isStudent: Boolean,
    $ampTraineeStatus: Boolean,
    $hasPartialSales: Boolean,
    $partialSaleLocations: [String!],
    $annualRevenueGt: Float,
    $annualRevenueLt: Float,
    $grossProductionGt: Float,
    $grossProductionLt: Float,
    $assetsUnderManagementGt: Float,
    $assetsUnderManagementLt: Float,
    $yearsInPracticeGt: Int,
    $yearsInPracticeLt: Int,
    $numberOfClientsGt: Int,
    $numberOfClientsLt: Int,
    $designations: [ID!], 
    $affiliations: [ID!], 
    $languages: [String!], 
    $advisorLicenses: [String!], 
    $ethnicity: String,
    $faith: String,
    $interests: [String!], 
    $sports: [String!], 
    $primaryMarkets: [String!], 
    $specializedBusinesses: [String!], 
    $tags: [String!], 
    $discretionaryInvestmentModels: Boolean,
    $isLimitedUser: Boolean,
    $name: String
  ) {
    getCurrentUser {
      id
      user_name
      features
      __typename
    }
    getCurrentUser {
      id
      motivation
      searchable_transition_goals
    }
    getGrowthTransitionGoals
    availableStrengths
    availableDesignations {
      id
      name
      __typename
    }
    company {
      id
      name
      country
      available_primary_markets
      features
      business_type
      avatar_background_color
      avatar_directory
      primary_color
      languages {
        id
        name
        __typename
      }
      interests {
        id
        name
        __typename
      }
      __typename
    }
    availableAffiliations {
      id
      name
      __typename
    }
    userSearch(
      prompt: $prompt
      page: $page
      perPage: $perPage
      locationWithin: $locationWithin
      transitionGoalsName: $transitionGoalsName
      planningPreference: $planningPreference
      province: $province
      isStudent: $isStudent
      ampTraineeStatus: $ampTraineeStatus
      hasPartialSales: $hasPartialSales
      partialSaleLocations: $partialSaleLocations
      annualRevenueGt: $annualRevenueGt
      annualRevenueLt: $annualRevenueLt
      grossProductionGt: $grossProductionGt
      grossProductionLt: $grossProductionLt
      assetsUnderManagementGt: $assetsUnderManagementGt
      assetsUnderManagementLt: $assetsUnderManagementLt
      yearsInPracticeGt: $yearsInPracticeGt
      yearsInPracticeLt: $yearsInPracticeLt
      numberOfClientsGt: $numberOfClientsGt
      numberOfClientsLt: $numberOfClientsLt
      designations: $designations
      affiliations: $affiliations
      languages: $languages
      advisorLicenses: $advisorLicenses
      ethnicity: $ethnicity
      faith: $faith
      interests: $interests
      sports: $sports
      primaryMarkets: $primaryMarkets
      specializedBusinesses: $specializedBusinesses
      tags: $tags
      discretionaryInvestmentModels: $discretionaryInvestmentModels
      isLimitedUser: $isLimitedUser
      name: $name
    ) {
      total_count
      items {
        id
        user_name
        allowed_name
        fb_user_name 
        annual_revenue
        assets_under_management
        bio
        city
        county
        show_city
        number_of_clients
        profile_picture
        province
        recurring_revenue
        created_at
        headline
        my_rating
        nick_name
        obfuscated
        is_pure_buyer
        transition_goals
        interested_in_selling
        expressed_interest
        profile_type
        years_practicing
        recognition_level
        gross_production
        tag_info
        annual_revenue
        map_latitude
        map_longitude
        __typename
        product_mix {
          id
          life_ifp
          home_ifp
          auto_ifp
          commercial_ifp
          health_ifp
          group_ifp
          mutual_funds_aum
          securities_aum
          annuities_aua
          total_assets
          health_and_life_stars
          aum_stars
          p_c_stars
          group_stars
          gic_fixed_income
          insurance
          equity
          __typename
        }
        note_count
        conversation_with_me {
          id
          __typename
        }
      }
      aiResponse
    }
  }
`;

const useStyles = makeStyles((theme) => ({
  property: {
    backgroundColor: '#fff',
    margin: theme.spacing(2),
    boxShadow: '0 1px 3px rgba(0,0,0,.1)',
  },
  propertyImage: {
    position: 'relative',
    height: '200px',
    overflow: 'hidden',
  },
  ribbon: {
    position: 'absolute',
    top: '20px',
    right: '-21px',
    padding: '3px 20px',
    backgroundColor: (props) => props.ribbonColor || '#333333',
    color: '#fff',
    textTransform: 'uppercase',
    transform: 'rotate(45deg)',
  },
  tagStatus: {
    position: 'absolute',
    top: '10px',
    left: '10px',
    padding: '3px 8px',
    backgroundColor: '#1396e2',
    color: '#fff',
  },
  aside: {
    padding: theme.spacing(2),
  },
  header: {
    marginBottom: theme.spacing(2),
  },
  userButton: {
    border: 'none',
    outline: 'none',
    color: '#1396e2',
    padding: 0,
    background: 'none',
    cursor: 'pointer',
    fontSize: '18px',
    fontWeight: 'bold',
  },
  location: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: '#5a5a5a',
  },
  dataList: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: theme.spacing(1),
    margin: theme.spacing(2, 0),
  },
  readMore: {
    border: 'none',
    outline: 'none',
    padding: 0,
    fontSize: '14px',
    color: '#1396e2',
    background: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  searchSidebar: {
    padding: theme.spacing(2),
    border: '1px solid rgba(0, 0, 0, 0.12)',
    borderRadius: 4,
  },
  accordion: {
    width: 300,
    marginBottom: theme.spacing(1),
  },
  formControl: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  slider: {
    marginTop: theme.spacing(4),
  },
  filterRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    overflowX: 'auto',
    padding: theme.spacing(1),
  },
  inlineAccordion: {
    minWidth: '200px',
    flex: '1 0 auto',
    margin: 0,
    '& .MuiAccordionSummary-root': {
      minHeight: '48px',
    },
    ai_copy: {
      fontSize: '14px',
      color: '#5a5a5a',
      marginTop: theme.spacing(2),
      padding: theme.spacing(2),
    }
  },
  searchBox: {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(2),
  },
  inputLabel: {
    position: 'relative',
    top: '-16px',
  },
  threeColumnGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: theme.spacing(2),
    width: '100%',
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    margin: theme.spacing(2),
  },
  ai_copy: {
    fontSize: '14px',
    color: '#5a5a5a',
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
  }
}));

const defaultSearchParams = {
  name: '',
  locationWithin: '',
  transitionGoalsName: [],
  planningPreference: '',
  province: '',
  isStudent: null,
  ampTraineeStatus: null,
  hasPartialSales: null,
  annualRevenueGt: null,
  annualRevenueLt: null,
  grossProductionGt: null,
  grossProductionLt: null,
  assetsUnderManagementGt: null,
  assetsUnderManagementLt: null,
  yearsInPracticeGt: null,
  yearsInPracticeLt: null,
  numberOfClientsGt: null,
  numberOfClientsLt: null,
  designations: [],
  affiliations: [],
  languages: [],
  advisorLicenses: [],
  ethnicity: '',
  faith: '',
  interests: [],
  sports: [],
  primaryMarkets: [],
  specializedBusinesses: [],
  tags: [],
  discretionaryInvestmentModels: null,
  isLimitedUser: null
};

const UserListSearch = () => {
  const classes = useStyles();
  const [searchParams, setSearchParams] = useState({
    prompt: '',
    locationWithin: '',
    transitionGoalsName: [],
    planningPreference: '',
    province: '',
    isStudent: null,
    ampTraineeStatus: null,
    hasPartialSales: null,
    annualRevenueGt: null,
    annualRevenueLt: null,
    grossProductionGt: null,
    grossProductionLt: null,
    assetsUnderManagementGt: null,
    assetsUnderManagementLt: null,
    yearsInPracticeGt: null,
    yearsInPracticeLt: null,
    numberOfClientsGt: null,
    numberOfClientsLt: null,
    designations: [],
    affiliations: [],
    languages: [],
    advisorLicenses: [],
    ethnicity: '',
    faith: '',
    interests: [],
    sports: [],
    primaryMarkets: [],
    specializedBusinesses: [],
    tags: [],
    discretionaryInvestmentModels: null,
    isLimitedUser: null
  });
  const [page, setPage] = useState(1);
  const perPage = 21;

  const { loading, error, data, refetch } = useQuery(USER_SEARCH_QUERY, {
    variables: {
      ...searchParams,
      page,
      perPage,
    },
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const [searchInputValue, setSearchInputValue] = useState('');
  const [conversationHistory, setConversationHistory] = useState([
    {type: 'ai',
    message: "Hi! I'm your search assistant tell me exactly what you're looking for!"}]);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);

  const formatConversationHistory = (history) => {
    return history.map(item => `${item.type === 'user' ? 'User' : 'AI'}: ${item.message}`).join('\n');
  };

  const handleSearchSubmit = () => {
    if (!searchInputValue.trim()) return;
    
    setIsWaitingForResponse(true);
    // Add user message to conversation
    const newUserMessage = {
      type: 'user',
      message: searchInputValue,
      timestamp: new Date().toISOString()
    };
    
    setConversationHistory(prev => [...prev, newUserMessage]);

    // Get recent conversation history (last 20 messages)
    const recentHistory = [...conversationHistory.slice(-19), newUserMessage];
    const historyString = formatConversationHistory(recentHistory);
    
    // Set the prompt in searchParams with history context
    setSearchParams(prev => ({
      ...prev,
      prompt: `Previous conversation:\n${historyString}\n\nCurrent request: ${searchInputValue}`
    }));
    
    setSearchInputValue('');
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault(); // Prevent default for multiline TextField
      handleSearchSubmit();
    }
  };

  const handleSearchChange = (field, value) => {
    // Convert string 'true'/'false' to actual boolean for boolean fields
    const booleanFields = ['isStudent', 'ampTraineeStatus', 'hasPartialSales', 'discretionaryInvestmentModels', 'isLimitedUser'];
    const processedValue = booleanFields.includes(field) && value !== null ? Boolean(value) : value;
    
    setSearchParams(prev => ({
      ...prev,
      [field]: processedValue
    }));
    setPage(1); // Reset to first page when search params change
  };

  // Update conversation when we get AI response
  React.useEffect(() => {
    if (data?.userSearch?.aiResponse && searchParams.prompt) {
      setIsWaitingForResponse(false);
      setConversationHistory(prev => {
        // Avoid duplicate responses
        if (prev.length > 0 && prev[prev.length - 1].message === data.userSearch.aiResponse) {
          return prev;
        }
        const newHistory = [...prev, {
          type: 'ai',
          message: data.userSearch.aiResponse,
          timestamp: new Date().toISOString()
        }];
        
        // Add count message
        if (data.userSearch.total_count > 0) {
          newHistory.push({
            type: 'ai',
            message: `${data.userSearch.total_count} advisors found.`,
            timestamp: new Date().toISOString()
          });
        } else {
          newHistory.push({
            type: 'ai',
            message: 'I couldn\'t find any advisors matching those criteria. Try broadening your search.',
            timestamp: new Date().toISOString()
          });
        }
        
        return newHistory;
      });
    }
  }, [data?.userSearch?.aiResponse]);

  if (error) return <div>Error: {error.message}</div>;

  const users = data?.userSearch?.items || [];
  const transitionGoals = data?.getCurrentUser?.searchable_transition_goals || [];
  const totalCount = data?.userSearch?.total_count || 0;
  const totalPages = Math.ceil(totalCount / perPage);
  const aiSearch = data?.company?.features?.includes('ai_search') || false;

  return (
    <Grid container spacing={0}>
      <Grid item xs={12} md={9}>
        {loading && (
          <div className={classes.loading}>
            <LinearProgress style={{ width: '100%' }} />
          </div>
        )}
        <div className="search-results">
          <Grid container spacing={0}>
            {users.map((user) => (
              <Grid item xs={12} sm={6} md={4} key={user.id}>
                <ListCard user={user} currentCompany={data?.company} />
              </Grid>
            ))}
          </Grid>

          <Grid container justify="center" spacing={0}>
            <Grid item>
              <Button 
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
            </Grid>
            <Grid item>
              <Typography>
                Page {page} of {totalPages} ({totalCount} total results)
              </Typography>
            </Grid>
            <Grid item>
              <Button 
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </Grid>
          </Grid>
        </div>
      </Grid>
      <Grid item xs={12} md={3}>
        <SearchSidebar
          layout="vertical"
          searchInputValue={searchInputValue}
          setSearchInputValue={setSearchInputValue}
          searchParams={searchParams}
          handleSearchChange={handleSearchChange}
          handleSearchSubmit={handleSearchSubmit}
          handleKeyPress={handleKeyPress}
          data={data}
          setSearchParams={setSearchParams}
          defaultSearchParams={defaultSearchParams}
          conversationHistory={conversationHistory}
          resetConversation={() => {
            setConversationHistory([
              {type: 'ai',
              message: "Hi! I'm your search assistant tell me exactly what you're looking for!"}
            ]);
            setSearchParams(defaultSearchParams);
            handleSearchSubmit();
          }}
          isLoading={isWaitingForResponse}
          refetch={refetch}
        />
        {aiSearch && <p className={classes.ai_copy}>
          The AI search may have unexpected results, you can always use the manual search if you're not finding what you're looking for.
        </p>}
      </Grid>
    </Grid>
  );
};

export default UserListSearch;